/**
 * AccountPage — RC 1.33.B tabs.
 *
 * RC 1.33.B quarantined the legacy credits tab: the old CreditsPage
 * exposed the pre-RC 1.32 "Starter / Growth / Power" credit packs +
 * Stripe checkout, which conflict with the new Plan + Usage model.
 * The Credits tab is removed from navigation and `/credits` now
 * redirects to this page's Plan tab — see App.js route for the
 * redirect. Plan is the single monetisation surface until RC 1.34
 * reintroduces top-ups properly.
 */
import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import SettingsPage from './SettingsPage';
import PrivacyPage from './PrivacyPage';
import AccountPlanSection from './AccountPlanSection';
import PreferencesCard from '@/components/PreferencesCard';

// RC 1.43.B — dedicated Preferences tab. Uses the same
// PreferencesCard the Settings tab renders, so there is one editor,
// one storage column, and one source of truth.
function PreferencesTab() {
  return (
    <div className="space-y-6" data-testid="account-panel-preferences-content">
      <PreferencesCard />
    </div>
  );
}

const TABS = [
  { key: 'plan',        label: 'Plan',        Component: AccountPlanSection },
  { key: 'preferences', label: 'Preferences', Component: PreferencesTab     },
  { key: 'settings',    label: 'Settings',    Component: SettingsPage       },
  { key: 'privacy',     label: 'Privacy',     Component: PrivacyPage        },
];

export default function AccountPage() {
  const [params, setParams] = useSearchParams();
  const active = params.get('tab') || 'plan';
  const Current = useMemo(
    () => TABS.find(t => t.key === active)?.Component || AccountPlanSection,
    [active],
  );

  return (
    <div className="space-y-6" data-testid="account-workspace">
      <header className="flex flex-col gap-1">
        <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">Workspace</p>
        <h1 className="text-3xl sm:text-4xl font-serif-display font-semibold text-ndor-ink tracking-tight">
          Account
        </h1>
        <p className="text-sm text-ndor-ink-secondary">
          Plan, profile preferences, and privacy controls.
        </p>
      </header>

      <Card className="border-ndor-edge bg-ndor-surface">
        <CardContent className="p-0">
          <nav
            className="flex border-b border-ndor-edge"
            data-testid="account-tabs"
            role="tablist"
          >
            {TABS.map(t => {
              const isActive = t.key === active;
              return (
                <Link
                  key={t.key}
                  to={`/account?tab=${t.key}`}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`account-tab-${t.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setParams({ tab: t.key });
                  }}
                  className={`relative flex-1 text-center px-4 py-4 text-sm transition-colors
                    ${isActive
                      ? 'text-ndor-ink font-semibold bg-ndor-surface-alt/50'
                      : 'text-ndor-ink-muted hover:text-ndor-ink hover:bg-ndor-surface-alt/30'}`}
                >
                  <span className="tracking-tight">{t.label}</span>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute left-6 right-6 bottom-0 h-[2px] bg-ndor-gold rounded-full"
                    />
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 sm:p-6" data-testid={`account-panel-${active}`}>
            <Current />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
